import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  QrCode, Plus, Trash2, Loader2, Pencil, Download, Grid3X3, Printer
} from 'lucide-react';

const TableManagement = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchCount, setBatchCount] = useState(5);
  const [batchPrefix, setBatchPrefix] = useState('Table');
  const [creating, setCreating] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [qrDialog, setQrDialog] = useState(null);
  const [qrData, setQrData] = useState(null);

  useEffect(() => { fetchTables(); }, []);

  const fetchTables = async () => {
    try {
      const res = await api.get('/tables');
      setTables(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const createBatch = async () => {
    if (batchCount < 1 || batchCount > 50) return;
    setCreating(true);
    try {
      await api.post('/tables/batch', { count: batchCount, prefix: batchPrefix || 'Table' });
      toast.success(`${batchCount} tables créées`);
      fetchTables();
    } catch (e) { toast.error('Erreur'); }
    finally { setCreating(false); }
  };

  const deleteTable = async (id) => {
    try {
      await api.delete(`/tables/${id}`);
      toast.success('Table supprimée');
      setTables(prev => prev.filter(t => t.id !== id));
    } catch (e) { toast.error('Erreur'); }
  };

  const saveEdit = async () => {
    if (!editDialog || !editLabel.trim()) return;
    try {
      await api.put(`/tables/${editDialog}`, { label: editLabel.trim() });
      toast.success('Table modifiée');
      setEditDialog(null);
      fetchTables();
    } catch (e) { toast.error('Erreur'); }
  };

  const showQr = async (table) => {
    setQrDialog(table);
    try {
      const res = await api.get(`/tables/${table.id}/qr`);
      setQrData(res.data);
    } catch (e) { toast.error('Erreur'); setQrDialog(null); }
  };

  const printQr = () => {
    if (!qrData) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>QR - ${qrDialog.label}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:40px}img{width:250px;height:250px}h2{margin-top:20px}</style></head>
      <body><img src="data:image/png;base64,${qrData.qr_code}" /><h2>${qrDialog.label}</h2><p style="color:#666;font-size:14px">Scannez pour commander</p></body></html>
    `);
    win.document.close();
    win.print();
  };

  if (loading) return <div className="flex items-center justify-center min-h-[300px]"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6" data-testid="table-management-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Grid3X3 className="h-8 w-8 text-orange-500" /> Gestion des tables
        </h1>
        <p className="text-gray-500 mt-1">Configurez vos tables et générez les QR codes pour la commande sur place</p>
      </div>

      {/* Batch creation */}
      <Card className="border-0 shadow-soft">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-sm">Préfixe</Label>
              <Input value={batchPrefix} onChange={e => setBatchPrefix(e.target.value)}
                placeholder="Table" className="w-32" data-testid="batch-prefix" />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Nombre</Label>
              <Input type="number" min={1} max={50} value={batchCount} onChange={e => setBatchCount(parseInt(e.target.value) || 1)}
                className="w-20" data-testid="batch-count" />
            </div>
            <Button onClick={createBatch} disabled={creating} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="batch-create-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Créer {batchCount} table{batchCount > 1 ? 's' : ''}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tables grid */}
      {tables.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Grid3X3 className="h-12 w-12 mx-auto mb-3" />
          <p>Aucune table configurée</p>
          <p className="text-sm mt-1">Créez vos tables pour générer les QR codes de commande</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {tables.map(table => (
            <Card key={table.id} className="border border-gray-100 hover:border-orange-200 transition-all group" data-testid={`table-card-${table.id}`}>
              <CardContent className="p-4 text-center space-y-2">
                <p className="font-heading font-semibold text-gray-900">{table.label}</p>
                <div className="flex justify-center gap-1">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => showQr(table)} data-testid={`table-qr-${table.id}`}>
                    <QrCode className="h-4 w-4 text-orange-500" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setEditDialog(table.id); setEditLabel(table.label); }}>
                    <Pencil className="h-4 w-4 text-gray-400" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-300 hover:text-red-500" onClick={() => deleteTable(table.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">{tables.length} table{tables.length > 1 ? 's' : ''} configurée{tables.length > 1 ? 's' : ''}</p>

      {/* Edit dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Modifier la table</DialogTitle></DialogHeader>
          <Input value={editLabel} onChange={e => setEditLabel(e.target.value)} data-testid="edit-table-label" />
          <DialogFooter>
            <Button onClick={saveEdit} className="bg-orange-500 hover:bg-orange-600 text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={!!qrDialog} onOpenChange={() => { setQrDialog(null); setQrData(null); }}>
        <DialogContent className="sm:max-w-sm text-center" data-testid="table-qr-dialog">
          <DialogHeader><DialogTitle>{qrDialog?.label}</DialogTitle></DialogHeader>
          {qrData ? (
            <div className="space-y-4">
              <img src={`data:image/png;base64,${qrData.qr_code}`} alt="QR" className="w-56 h-56 mx-auto rounded-xl shadow" />
              <p className="text-xs text-gray-400 font-mono break-all">{qrData.url}</p>
              <p className="text-sm text-gray-600">Scannez pour commander depuis cette table</p>
              <Button onClick={printQr} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="print-qr-btn">
                <Printer className="h-4 w-4 mr-2" /> Imprimer le QR code
              </Button>
            </div>
          ) : (
            <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto my-8" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TableManagement;
